import React from 'react';

export const CONTACT_EMAIL = 'customersupport@appancestor.com';

const Mail = () => <a className="u-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

/**
 * Both language editions of the EyesDwell policy. Each edition carries its own
 * chrome labels plus an ordered list of blocks, so the page renders one way and
 * only the content differs.
 * @author Nelson
 */
const content = {
    en: {
        htmlLang: 'en',
        docTitle: 'EyesDwell – Privacy Policy',
        title: 'EyesDwell – Privacy Policy',
        back: 'Go Back',
        effectiveLabel: 'Effective',
        updatedLabel: 'Last Updated',
        effective: 'August 27, 2026',
        updated: 'August 27, 2026',
        developerLabel: 'Developer',
        blocks: [
            ['p', <>EyesDwell is an iPhone-only app for iOS 16 and later, developed by <strong>YUAN LAB ENTERPRISE</strong>, Malaysia.</>],
            ['p', <>If you have questions about this Privacy Policy, contact us at <Mail />.</>],

            ['h3', '1. Camera and Face Data'],
            ['p', <>EyesDwell uses the iPhone’s front camera to measure where your gaze stays during an eight-second measurement.</>],
            ['p', <>Camera images are processed <strong>frame by frame on your device</strong> to derive facial landmark coordinates. Images are not saved, added to your Photos library, uploaded, or sent to any server. There is no backend server for this processing. Each frame is discarded immediately after processing.</>],
            ['p', <>EyesDwell does <b>not</b> perform facial recognition, identify you, create a facial template or face profile, or compare your face with any database.</>],
            ['p', <>During measurement, EyesDwell compares the apparent size and position of the face between adjacent frames. This is used only to detect when tracking may have switched to another person’s face and to stop the measurement. This is a comparison of the camera frames, <strong>not identity recognition</strong>.</>],
            ['p', <>EyesDwell has no account system and no login. Camera access is required for the app to operate. If you deny camera permission, the measurement feature cannot work.</>],

            ['h3', '2. What We Do Not Collect'],
            ['p', <>EyesDwell does not collect or retain:</>],
            ['ul', [
                'Your name or email address',
                'An account or login information',
                'Your location',
                'Your contacts',
                'Health information',
                'Camera images or facial data'
            ]],
            ['p', <>EyesDwell itself does not make network requests and does not use third-party analytics tools.</>],
            ['p', <>The app stores only the following preferences locally on your device:</>],
            ['ul', [
                'Calibration baseline, which is one number',
                'Whether the guidance has been viewed',
                'First-round ad flag',
                'The time the last ad was viewed',
                'Ad-removal purchase status',
                'Whether the privacy-settings entry should be shown',
                'Language preference',
                'Mute preference'
            ]],
            ['p', <>Deleting EyesDwell removes these locally stored preferences.</>],
            ['p', <><strong>Google’s advertising and consent SDKs may maintain their own local storage. That storage is managed by Google and is separate from the local preferences listed above.</strong></>],

            ['h3', '3. Google AdMob'],
            ['p', <>EyesDwell may display rewarded advertisements through <strong>Google AdMob</strong>. AdMob may collect device identifiers and advertising interaction data for purposes such as serving ads, controlling ad frequency, and preventing fraud.</>],
            ['p', <>In regions where consent is legally required, such as the European Union and the United Kingdom, the app shows a consent dialog. Users in those regions can later return to <strong>“How it works · Privacy”</strong> within the app to change their privacy choices when that entry is available to them.</>],
            ['p', <>For more information, see Google’s <a className="u-link" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and <a className="u-link" href="https://adssettings.google.com/" target="_blank" rel="noreferrer">Ads Settings</a>.</>],

            ['h4', 'Tracking and Advertising ID'],
            ['p', <>EyesDwell does not request Apple’s App Tracking Transparency permission.</>],
            ['p', <>EyesDwell does not perform cross-app or cross-website tracking and does not obtain or use Apple’s IDFA advertising identifier. Ads are not personalized by EyesDwell using your advertising identifier.</>],

            ['h3', '4. In-App Purchase'],
            ['p', <>EyesDwell offers a one-time purchase to <strong>remove ads</strong>. It is not a subscription and does not require an account.</>],
            ['p', <>Payment is handled entirely by Apple. YUAN LAB ENTERPRISE does not see your payment information.</>],
            ['p', <>After the ad-removal purchase, EyesDwell no longer initializes the advertising SDK.</>],

            ['h3', '5. Apple-Shared Diagnostics and Usage Data'],
            ['p', <>If you choose in iOS settings to share analytics with app developers, Apple may provide us with crash reports and usage statistics that do not identify you.</>],

            ['h3', '6. Children'],
            ['p', <>EyesDwell is not directed to children under 13.</>],

            ['h3', '7. Your Choices and Rights'],
            ['p', <>Because EyesDwell does not have user accounts, there is no account-based data portal.</>],
            ['p', <>EyesDwell processes camera images only on your device. Neither we nor any third party receives, stores, or transmits those images. The legal basis for that processing is your consent, which you give by granting camera permission and which you can withdraw at any time in iOS Settings. We do not hold any personal data about you.</>],
            ['p', <>For Google AdMob’s collection of device identifiers, the legal basis in regions where consent is required is your consent. In other regions, the legal basis is determined under applicable local law.</>],
            ['p', <>If you are in the European Union or the United Kingdom, you have the right to lodge a complaint with the data protection supervisory authority in the country where you are located.</>],
            ['p', <>Advertising data processed by Google may be transferred to and stored in countries outside Malaysia, including the United States. These transfers are made using safeguards provided by Google.</>],
            ['p', <>If you are a California resident, sharing device identifiers for advertising may constitute “sharing” under California law. Where applicable, you can withdraw advertising consent through the in-app privacy settings or contact us by email to make a request.</>],
            ['p', <>Google handles the retention period for advertising data according to Google’s own data retention policies.</>],
            ['p', <>Depending on your location, you can:</>],
            ['ul', [
                'Withdraw advertising consent through the in-app privacy settings when that option is provided.',
                'Delete the app to remove its locally stored preferences.',
                <>Contact us by email at <Mail /> with questions or requests concerning your data.</>
            ]],

            ['h3', '8. Changes to This Policy'],
            ['p', <>If this Privacy Policy changes, we will update this page and the <strong>Last Updated</strong> date above.</>]
        ]
    },

    zh: {
        htmlLang: 'zh-Hans',
        docTitle: 'EyesDwell – 隐私政策',
        title: 'EyesDwell – 隐私政策',
        back: '返回',
        effectiveLabel: '生效日期',
        updatedLabel: '最后更新',
        effective: '2026年8月27日',
        updated: '2026年8月27日',
        developerLabel: '开发者',
        blocks: [
            ['p', <>EyesDwell 是一款仅适用于 iPhone、运行于 iOS 16 及以上版本的 App，由马来西亚 <strong>YUAN LAB ENTERPRISE</strong> 开发。</>],
            ['p', <>如对本隐私政策有任何疑问，请通过电子邮件联系：<Mail />。</>],

            ['h3', '1. 相机与脸部资料'],
            ['p', <>EyesDwell 使用 iPhone 前置相机，在八秒的量测过程中判断你的视线主要停留在哪一侧。</>],
            ['p', <>相机影像会<strong>逐帧在你的装置上处理</strong>，并从影像中推导脸部关键点座标。影像不会被保存、存入相簿、上传或发送到任何伺服器。此处理没有后端伺服器参与。每一帧处理完成后会立即丢弃。</>],
            ['p', <>EyesDwell <b>不会</b>进行脸部辨识，不会识别你的身份，不会建立脸部范本或脸部特征档案，也不会与任何资料库进行比对。</>],
            ['p', <>量测过程中，EyesDwell 会比较相邻两帧中脸部的大小与位置，用来发现脸部追踪是否可能跳到了其他人的脸上，并在这种情况下中止量测。这只是对相邻画面的比较，<strong>不是身份辨识</strong>。</>],
            ['p', <>EyesDwell 没有帐号或登入功能。相机权限是 App 正常运作所必需的。如果你拒绝相机权限，量测功能将无法使用。</>],

            ['h3', '2. 我们不收集什么'],
            ['p', <>EyesDwell 不会收集或保存：</>],
            ['ul', [
                '姓名或电子邮件地址',
                '帐号或登入资料',
                '位置资料',
                '通讯录',
                '健康资料',
                '相机影像或脸部资料'
            ]],
            ['p', <>EyesDwell 本身不会发出网路请求，也没有使用第三方分析工具。</>],
            ['p', <>App 只会在你的装置上保存以下偏好：</>],
            ['ul', [
                '校零基准：一个数字',
                '是否看过引导',
                '首轮广告标志',
                '上次看广告的时间',
                '移除广告的买断状态',
                '是否需要显示隐私设定入口',
                '语言偏好',
                '静音偏好'
            ]],
            ['p', <>删除 EyesDwell 后，这些本机保存的偏好会一并清除。</>],
            ['p', <><strong>Google 的广告与同意 SDK 可能会自行进行本机储存。这部分储存由 Google 管理，与上述由 EyesDwell 保存的偏好分开。</strong></>],

            ['h3', '3. Google AdMob'],
            ['p', <>EyesDwell 会显示由 <strong>Google AdMob</strong> 提供的奖励广告。AdMob 可能会收集装置识别码和广告互动资料，用于播放广告、控制广告频次和防止诈欺等目的。</>],
            ['p', <>在法律要求取得同意的地区（例如欧盟、英国等），App 会显示同意视窗。这些地区的使用者在 App 内可以回到<strong>「怎么玩 · 隐私设定」</strong>，在该入口适用时改变自己的隐私选择。</>],
            ['p', <>更多资讯请参阅 Google 的<a className="u-link" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">隐私权政策</a>与<a className="u-link" href="https://adssettings.google.com/" target="_blank" rel="noreferrer">广告设定</a>。</>],

            ['h4', '追踪与广告识别码'],
            ['p', <>EyesDwell 不会请求 Apple 的 App Tracking Transparency 授权。</>],
            ['p', <>EyesDwell 不进行跨 App 或跨网站追踪，也不会取得或使用 Apple 的 IDFA 广告识别码。EyesDwell 不会根据你的广告识别码对广告进行个性化。</>],

            ['h3', '4. App 内购买'],
            ['p', <>EyesDwell 提供一次性买断的「移除广告」功能。该购买不是订阅，也不需要帐号。</>],
            ['p', <>付款完全由 Apple 处理。YUAN LAB ENTERPRISE 不会看到你的付款资料。</>],
            ['p', <>买断「移除广告」后，EyesDwell 将不再初始化广告 SDK。</>],

            ['h3', '5. Apple 分享的当机与使用统计'],
            ['p', <>如果你在 iOS 设定中选择与 App 开发者分享分析资料，Apple 可能会向我们提供不含你身份的当机报告与使用统计。</>],

            ['h3', '6. 儿童'],
            ['p', <>EyesDwell 不针对 13 岁以下儿童。</>],

            ['h3', '7. 你的选择与权利'],
            ['p', <>由于 EyesDwell 没有帐号，因此没有以帐号为基础的资料管理入口。</>],
            ['p', <>EyesDwell 只在你的装置上处理相机影像。我们与任何第三方都不会收到、保存或传输这些影像。该处理的法律依据是你的同意，也就是你授予相机权限这个动作，你可以随时在 iOS 设定中撤回。我们不持有任何关于你的个人资料。</>],
            ['p', <>对于 Google AdMob 收集装置识别码，在需要取得同意的地区，法律依据是使用者的同意。在其他地区，法律依据依当地适用法律决定。</>],
            ['p', <>如果你位于欧盟或英国，你有向所在国家的资料保护监督机关提出申诉的权利。</>],
            ['p', <>广告资料由 Google 处理，可能被传输并存放于马来西亚以外的国家，包括美国。该传输依 Google 自身提供的保障措施进行。</>],
            ['p', <>如果你是加州居民，为播放广告而分享装置识别码，在加州法下可能构成「分享」。在适用地区，你可以透过 App 内的隐私设定撤回广告同意，或来信提出请求。</>],
            ['p', <>Google 就广告资料的保留期限，依 Google 自己的资料保留政策处理。</>],
            ['p', <>根据你所在的地区，你可以：</>],
            ['ul', [
                '在适用地区透过 App 内的隐私设定撤回广告同意。',
                '删除 App，以清除 EyesDwell 在装置上保存的偏好。',
                <>如对个人资料有疑问或提出相关请求，可透过电子邮件联系：<Mail />。</>
            ]],

            ['h3', '8. 隐私政策的变更'],
            ['p', <>如本隐私政策发生变更，我们会更新本页面，并更新上方的<strong>最后更新日期</strong>。</>]
        ]
    }
};

export default content;
